import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../utils/translations';
import supabase from '../../lib/supabase';
import { toast } from "sonner"
import Button from "../Common/Button"

const { FiEdit3, FiSave, FiX, FiUpload } = FiIcons;

const ProfilePage = ({ setActiveTab }) => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    avatarUrl: user?.avatarUrl || ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(user?.avatarUrl || '');
  const [loading, setLoading] = useState(false);
  const t = useTranslation(user?.language || 'fr'); // Default to French
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Update preview URL when user.avatarUrl changes
    setPreviewUrl(user?.avatarUrl || '');
    setFormData({
      fullName: user?.fullName || '',
      email: user?.email || '',
      avatarUrl: user?.avatarUrl || ''
    });
  }, [user]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/i)) {
      toast.error(t('profile.invalidImageFile', 'Please select a valid image file (PNG, JPG, GIF, or WebP)'));
      return;
    }

    // Validate file size (max 5MB, matching AgentsManagementPage)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('profile.imageSizeTooLarge', 'Image must be less than 5MB'));
      return;
    }

    setImageFile(file);

    // Create a preview URL
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadAvatarImage = async () => {
    if (!imageFile) return formData.avatarUrl;

    setLoading(true);
    try {
      // Generate a unique filename
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `avatars/${user.id}/${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('assets')
        .upload(filePath, imageFile, { cacheControl: '3600', upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('assets')
        .getPublicUrl(filePath);

      if (!publicUrlData || !publicUrlData.publicUrl) {
        throw new Error(t('profile.imageUrlError', 'Failed to get public URL for uploaded image'));
      }

      return publicUrlData.publicUrl;
    } catch (error) {
      console.error('Error uploading avatar image:', error);
      toast.error(t('profile.uploadImageError', 'Error uploading avatar image'));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload image if selected
      let avatarUrl = formData.avatarUrl;
      if (imageFile) {
        avatarUrl = await uploadAvatarImage();
      }

      // Update email if changed
      if (formData.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: formData.email
        });
        if (emailError) throw emailError;
      }

      // Update user profile in context and database
      await updateUser({
        fullName: formData.fullName,
        avatarUrl
      });

      toast.success(t('profile.profileUpdated', 'Profile updated successfully'));
      setIsEditing(false);
      setImageFile(null);
      setPreviewUrl(avatarUrl);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('profile.updateProfileError', 'Error updating profile'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: user?.fullName || '',
      email: user?.email || '',
      avatarUrl: user?.avatarUrl || ''
    });
    setImageFile(null);
    setPreviewUrl(user?.avatarUrl || '');
    setIsEditing(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-12xl mx-auto"
      >
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-white">
            {t('profile.profile', 'Profile')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('profile.subtitle', 'Manage your personal information')}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-xs border border-gray-200 p-6 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('profile.personalInformation', 'Personal Information')}
            </h2>
            {!isEditing && (
              <Button
                onClick={() => setIsEditing(true)}
                icon={FiEdit3}
                responsive
              >
                {t('profile.editProfile', 'Edit Profile')}
              </Button>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Avatar */}
              <div className="flex items-center space-x-6">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200">
                  <img
                    src={previewUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.fullName || 'User')}&background=0ea5e9&color=fff`}
                    alt={t('profile.profilePictureAlt', 'Profile')}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.fullName || 'User')}&background=0ea5e9&color=fff`;
                    }}
                  />
                </div>
                {isEditing && (
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('profile.profilePicture', 'Profile Picture')}
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="file"
                        id="avatar-image"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleImageChange}
                        className="hidden"
                        ref={fileInputRef}
                      />
                      <label
                        htmlFor="avatar-image"
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      >
                        <SafeIcon icon={FiUpload} className="w-4 h-4" />
                        <span>{t('profile.uploadImage', 'Upload Image')}</span>
                      </label>
                    </div>
                    {imageFile && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {imageFile.name} ({(imageFile.size / 1024).toFixed(1)} KB)
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('profile.fullName', 'Full Name')}
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                ) : (
                  <div className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900 dark:bg-gray-700 dark:text-gray-300">
                    {formData.fullName || t('profile.na', 'N/A')}
                  </div>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('profile.email', 'Email')}
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                ) : (
                  <div className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900 dark:bg-gray-700 dark:text-gray-300">
                    {formData.email || t('profile.na', 'N/A')}
                  </div>
                )}
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('profile.language', 'Language')}
                </label>
                <div className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900 dark:bg-gray-700 dark:text-gray-300">
                  {user?.language === 'fr' ? t('profile.french', 'French') : t('profile.english', 'English')}
                </div>
              </div>
            </div>

            {isEditing && (
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <Button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"                  
                >
                  {t('profile.cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  loading={loading}
                  icon={FiSave}                  
                >
                  {t('profile.save', 'Save')}
                </Button>
              </div>
            )}
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default ProfilePage;